export const noop = (): void => {};
export const noopAsync = async (): Promise<any> => {};

export interface DebounceOptions {
    leading?: boolean;
    trailing?: boolean;
    maxWait?: number;
}

export interface DebouncedFunc<T extends (...args: any[]) => any> {
    (...args: Parameters<T>): ReturnType<T> | undefined;
    cancel(): void;
    flush(): ReturnType<T> | undefined;
}

export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number,
    options?: DebounceOptions,
): DebouncedFunc<T> {
    let lastArgs: Parameters<T> | undefined;
    let lastThis: unknown;
    let result: ReturnType<T> | undefined;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    let lastCallTime: number | undefined;
    let lastInvokeTime = 0;

    const leading = options?.leading ?? false;
    const trailing = options?.trailing ?? true;
    const hasMaxWait = options?.maxWait !== undefined;
    const maxWait = hasMaxWait ? Math.max(options?.maxWait ?? 0, wait) : 0;

    function invokeFunc(time: number): ReturnType<T> {
        const args = lastArgs!;
        const thisArg = lastThis;

        lastArgs = undefined;
        lastThis = undefined;
        lastInvokeTime = time;
        result = func.apply(thisArg, args) as ReturnType<T>;
        return result!;
    }

    function startTimer(pendingFunc: () => void, waitMs: number) {
        timerId = setTimeout(pendingFunc, waitMs);
    }

    function cancelTimer() {
        if (timerId !== undefined) {
            clearTimeout(timerId);
        }
    }

    function leadingEdge(time: number): ReturnType<T> | undefined {
        lastInvokeTime = time;
        startTimer(timerExpired, wait);
        return leading ? invokeFunc(time) : result;
    }

    function remainingWait(time: number): number {
        const timeSinceLastCall = time - (lastCallTime ?? 0);
        const timeSinceLastInvoke = time - lastInvokeTime;
        const timeWaiting = wait - timeSinceLastCall;

        return hasMaxWait
            ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
            : timeWaiting;
    }

    function shouldInvoke(time: number): boolean {
        const timeSinceLastCall =
            lastCallTime === undefined ? undefined : time - lastCallTime;
        const timeSinceLastInvoke = time - lastInvokeTime;

        return (
            lastCallTime === undefined ||
            timeSinceLastCall! >= wait ||
            timeSinceLastCall! < 0 ||
            (hasMaxWait && timeSinceLastInvoke >= maxWait)
        );
    }

    function timerExpired(): void {
        const time = Date.now();
        if (shouldInvoke(time)) {
            trailingEdge(time);
            return;
        }
        startTimer(timerExpired, remainingWait(time));
    }

    // Matches lodash: timerId is nullified without clearTimeout.
    // If called from flush(), the orphaned timer fires timerExpired → trailingEdge
    // as a harmless no-op because lastArgs has already been cleared.
    function trailingEdge(time: number): ReturnType<T> | undefined {
        timerId = undefined;

        if (trailing && lastArgs) {
            return invokeFunc(time);
        }
        lastArgs = undefined;
        lastThis = undefined;
        return result;
    }

    function cancel(): void {
        cancelTimer();
        lastInvokeTime = 0;
        lastArgs = undefined;
        lastCallTime = undefined;
        lastThis = undefined;
        timerId = undefined;
    }

    function flush(): ReturnType<T> | undefined {
        return timerId === undefined ? result : trailingEdge(Date.now());
    }

    function debounced(
        this: unknown,
        ...args: Parameters<T>
    ): ReturnType<T> | undefined {
        const time = Date.now();
        const isInvoking = shouldInvoke(time);

        lastArgs = args;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        lastThis = this;
        lastCallTime = time;

        if (isInvoking) {
            if (timerId === undefined) {
                return leadingEdge(lastCallTime);
            }
            if (hasMaxWait) {
                cancelTimer();
                startTimer(timerExpired, wait);
                return invokeFunc(lastCallTime);
            }
        }
        if (timerId === undefined) {
            startTimer(timerExpired, wait);
        }
        return result;
    }

    debounced.cancel = cancel;
    debounced.flush = flush;

    return debounced;
}
